import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, ChevronDown, ChevronUp, Trash2, Database, X } from 'lucide-react';
import { TruSpaceIconBadge } from '@/components/brand/TruSpaceIconBadge';
import { cn } from '@/lib/cn';
import { useAiChat } from '../hooks/useAiChat';
import type { ChatMessage } from '../types';
import { InsightCards } from './InsightCards';
import { MarkdownLite } from './MarkdownLite';

const WELCOME_MESSAGE_ID = 'welcome';
const AI_DISCLAIMER = 'TruInsight AI can make mistakes. Check important figures on the linked page.';

/** TruSpace logo avatar used in header and message bubbles */
function TsAvatar({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  return <TruSpaceIconBadge size={size === 'md' ? 'md' : 'sm'} />;
}

interface AiChatPanelProps {
  onClose?: () => void;
  isFloating?: boolean;
  /** Dashboard tile — WidgetShell owns the card chrome; hide the inner header title. */
  embedded?: boolean;
}

export function AiChatPanel({ onClose, isFloating = false, embedded = false }: AiChatPanelProps) {
  const { messages, isLoading, sendMessage, clearHistory, isSSI, scopeLabel, suggestions } = useAiChat();
  const [input, setInput] = useState('');
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const hasAutoScrolledRef = useRef(false);

  // Keep the latest message in view by scrolling ONLY the messages
  // container — never `scrollIntoView`, which would also scroll the
  // panel's ancestors (the window), yanking the whole dashboard to the
  // bottom when this panel is embedded as the NL Query widget.
  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    // Don't auto-scroll on the very first render; only follow new messages.
    if (!hasAutoScrolledRef.current) {
      hasAutoScrolledRef.current = true;
      return;
    }
    container.scrollTop = container.scrollHeight;
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || isLoading) return;
    setInput('');
    sendMessage(trimmed);
  };

  return (
    <div
      style={isFloating ? {
        // Respect iOS safe-area insets so the floating panel never
        // sits under the notch / home indicator on phones.
        bottom: 'max(1rem, env(safe-area-inset-bottom))',
        right: 'max(1rem, env(safe-area-inset-right))',
        left: 'max(1rem, env(safe-area-inset-left))',
        top: 'max(1rem, env(safe-area-inset-top))',
      } : undefined}
      className={cn(
        'flex flex-col',
        embedded
          ? 'h-full min-h-0 bg-transparent'
          : 'bg-background border border-border',
        !embedded && isFloating
          // On phones the panel fills the viewport with a small
          // inset; from sm+ it shrinks back to a fixed 420×600
          // floating window anchored to the bottom-right.
          ? 'fixed z-50 rounded-xl shadow-2xl sm:!left-auto sm:!top-auto sm:!right-4 sm:!bottom-4 sm:w-[420px] sm:h-[600px]'
          : !embedded && 'h-full rounded-xl shadow-sm',
      )}
    >
      <div
        className={cn(
          'flex items-center justify-between border-b border-border',
          embedded ? 'px-3 py-2' : 'px-4 py-3',
        )}
      >
        {embedded ? (
          <p className="min-w-0 truncate text-xs text-muted-foreground">
            Answering for <span className="font-medium">{scopeLabel}</span>
          </p>
        ) : (
          <div className="flex items-center gap-2">
            <TsAvatar size="md" />
            <div>
              <h3 className="text-sm font-semibold text-foreground">TruInsight AI</h3>
              <p className="text-[10px] text-muted-foreground">
                Answering for <span className="font-medium">{scopeLabel}</span>
              </p>
            </div>
          </div>
        )}
        <div className="flex items-center gap-1">
          <button
            onClick={clearHistory}
            className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            title="Clear conversation"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} showSqlDisclosure={isSSI} />
        ))}
      </div>

      {/* Quick suggestions (show when few messages) */}
      {messages.length <= 2 && (
        <div className="flex flex-wrap gap-1.5 px-4 pb-2">
          {suggestions.map((q) => (
            <button
              key={q}
              onClick={() => sendMessage(q)}
              disabled={isLoading}
              className="rounded-full border border-border bg-background px-2.5 py-1 text-[10px] text-muted-foreground transition-colors hover:border-gray-400 hover:text-gray-600"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-border px-3 py-2.5">
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about your portfolio..."
            disabled={isLoading}
            className="flex-1 rounded-lg border border-border bg-muted px-3 py-2 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground focus:border-gray-500 focus:ring-1 focus:ring-gray-500"
          />
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className={cn(
              'flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-colors',
              input.trim() && !isLoading
                ? 'bg-gray-600 text-white hover:bg-gray-500'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Message Bubble ─────────────────────────────────────────────

function MessageBubble({
  message,
  showSqlDisclosure,
}: {
  message: ChatMessage;
  /** Only SSI/internal users see the raw generated SQL; hidden for everyone else. */
  showSqlDisclosure: boolean;
}) {
  const [showSql, setShowSql] = useState(false);
  const [showData, setShowData] = useState(false);

  if (message.isLoading && !message.content && !message.cards?.length) {
    return (
      <div className="flex gap-2">
        <TsAvatar />
        <div className="rounded-lg bg-muted px-3 py-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Analyzing your portfolio data...
          </div>
        </div>
      </div>
    );
  }

  if (message.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-lg bg-gray-600 px-3 py-2 text-sm text-white">
          {message.content}
        </div>
      </div>
    );
  }

  // Assistant message
  return (
    <div className="flex gap-2">
      <TsAvatar />
      <div className="min-w-0 max-w-[90%] space-y-2">
        {/* Main answer (rendered Markdown — bold, lists, inline code) */}
        {message.content ? (
          <div className="rounded-lg bg-muted px-3 py-2 text-sm text-foreground">
            <MarkdownLite content={message.content} links={message.links} />
          </div>
        ) : message.isLoading ? (
          <div className="rounded-lg bg-muted px-3 py-2">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Analyzing your portfolio data...
            </div>
          </div>
        ) : null}

        {message.cards && message.cards.length > 0 ? <InsightCards cards={message.cards} /> : null}

        {/* Data table (collapsible) */}
        {message.data && message.data.length > 0 && (
          <div>
            <button
              onClick={() => setShowData(!showData)}
              className="flex items-center gap-1 text-[10px] font-medium text-gray-600 hover:text-gray-500"
            >
              <Database className="h-3 w-3" />
              {showData ? 'Hide' : 'Show'} data ({message.data.length} rows)
              {showData ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showData && <DataTable rows={message.data} />}
          </div>
        )}

        {/* SQL query (collapsible) — internal/SSI only */}
        {showSqlDisclosure && message.sqlQuery && (
          <div>
            <button
              onClick={() => setShowSql(!showSql)}
              className="flex items-center gap-1 text-[10px] font-medium text-muted-foreground hover:text-muted-foreground"
            >
              SQL Query
              {showSql ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {showSql && (
              <pre className="mt-1 overflow-x-auto rounded-md bg-gray-900 p-2 text-[10px] text-green-400">
                {message.sqlQuery}
              </pre>
            )}
          </div>
        )}

        {/* Error */}
        {message.error && (
          <div className="rounded-md bg-red-50 px-2 py-1 text-[10px] text-red-600 dark:bg-red-900/20 dark:text-red-400">
            {message.error}
          </div>
        )}

        {!message.isLoading &&
          message.id !== WELCOME_MESSAGE_ID &&
          Boolean(message.content || message.cards?.length || message.error) && (
          <p className="text-[10px] text-muted-foreground">{AI_DISCLAIMER}</p>
        )}
      </div>
    </div>
  );
}

// ─── Data Table ─────────────────────────────────────────────────

function DataTable({ rows }: { rows: Record<string, unknown>[] }) {
  if (rows.length === 0) return null;

  const columns = Object.keys(rows[0]);
  const displayRows = rows.slice(0, 20); // Limit display

  return (
    <div className="mt-1 overflow-x-auto rounded-md border border-border">
      <table className="w-full text-left text-[10px]">
        <thead>
          <tr className="border-b border-border bg-muted">
            {columns.map((col) => (
              <th key={col} className="whitespace-nowrap px-2 py-1 font-medium text-muted-foreground">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {displayRows.map((row, i) => (
            <tr key={i} className="hover:bg-muted/50">
              {columns.map((col) => (
                <td key={col} className="whitespace-nowrap px-2 py-1 text-foreground">
                  {formatCellValue(row[col])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 20 && (
        <div className="border-t border-border px-2 py-1 text-[9px] text-muted-foreground">
          Showing 20 of {rows.length} rows
        </div>
      )}
    </div>
  );
}

function formatCellValue(val: unknown): string {
  if (val === null || val === undefined) return '—';
  if (typeof val === 'number') {
    if (Number.isInteger(val) && Math.abs(val) > 999) {
      return val.toLocaleString();
    }
    if (!Number.isInteger(val)) {
      return val.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }
  }
  return String(val);
}
